// Putting a GLB on screen: the three.js half of every mesh view in the app.
//
// Two things draw meshes now — the full viewer, where one mesh is judged, and the run
// thumbnails, where several are compared at a glance — and they agree on everything
// except whether you can turn the object. Lights, framing, the render loop and the
// teardown are the same work, and the framing especially is not the kind of thing to
// have two copies of: it is what stops a screw and a bar stool, both "1 unit" to nobody,
// from being drawn as a dot and as an interior.
//
// What is NOT here is the wireframe and the triangle counts. Those answer "how is this
// mesh built", which is the viewer's question alone; a thumbnail that carried them would
// be paying for an answer nobody reads at 96 pixels.

// three.js is ~600 KB and only these views need it, so it is imported on first use and
// shared afterwards. The loader and the controls are separate modules, and importing
// them together keeps a viewer from rendering before it can be driven.
let deps: Promise<{
  THREE: typeof import('three');
  GLTFLoader: typeof import('three/examples/jsm/loaders/GLTFLoader.js')['GLTFLoader'];
  OrbitControls: typeof import('three/examples/jsm/controls/OrbitControls.js')['OrbitControls'];
  RoomEnvironment: typeof import('three/examples/jsm/environments/RoomEnvironment.js')['RoomEnvironment'];
}> | null = null;

// ONE KTX2 LOADER FOR THE PAGE (its transcoder and workers made once), told which compressed formats
// this device's GPU takes. Its own way of asking wants a renderer, and the GLTF loader is not tied to
// one (one decoded mesh, many views): so it asks a throwaway WebGL 2 context, the same questions,
// and gives the context back.
function ktx2Loader(KTX2Loader: typeof import('three/examples/jsm/loaders/KTX2Loader.js')['KTX2Loader']) {
  const loader = new KTX2Loader();
  const canvas = document.createElement('canvas');
  const gl = canvas.getContext('webgl2');
  if (gl) {
    const extensions = { has: (name: string) => !!gl.getExtension(name), get: (name: string) => gl.getExtension(name) };
    loader.detectSupport({ extensions } as unknown as import('three').WebGLRenderer);
    gl.getExtension('WEBGL_lose_context')?.loseContext();
  }
  return loader;
}

export function loadDeps() {
  if (!deps) {
    deps = Promise.all([
      import('three'),
      import('three/examples/jsm/loaders/GLTFLoader.js'),
      import('three/examples/jsm/controls/OrbitControls.js'),
      // A room, BUILT IN CODE: a handful of coloured boxes three.js ships as a module,
      // rendered once into a cube map — a soft, many-directional reflection without shipping
      // an HDR. It is one of the two lightings below, and no longer the only one.
      import('three/examples/jsm/environments/RoomEnvironment.js'),
      // A packed mesh's decoder (see meshLoader): a few KB of WebAssembly.
      import('three/examples/jsm/libs/meshopt_decoder.module.js'),
      // And its textures' (KTX2, Basis): the transcoder, fetched the first time a file has one.
      import('three/examples/jsm/loaders/KTX2Loader.js'),
    ]).then(([THREE, loader, controls, room, meshopt, ktx2]) => {
      // ON THE PUBLISHED TUTORIAL, EVERY FILE DOWNLOADED ONCE AND KEPT, in three's own memory
      // cache (Louis 2026-10-23): the colourways share their shapes, and a private window on an
      // iPhone downloaded all eleven again for each chair, 2.2 MB and a second's wait, the
      // browser's HTTP cache not answering the loader's requests there. A page of small packed
      // meshes, so the memory is a few MB. Not the app, whose meshes are bigger and many.
      if (isPublicTutorialRoute()) THREE.Cache.enabled = true;
      tallyFileCache(THREE.Cache);
      return {
        THREE,
        GLTFLoader: packedLoader(THREE, loader.GLTFLoader, meshopt.MeshoptDecoder, ktx2Loader(ktx2.KTX2Loader)),
        OrbitControls: controls.OrbitControls,
        RoomEnvironment: room.RoomEnvironment,
      };
    });
  }
  return deps;
}

// HOW THE MESH IS LIT, and it is a question with two answers rather than a setting with a
// right value.
//
// EVEN is a uniform white sphere of light: every surface gets the same irradiance whichever
// way it points, so what is on screen IS the albedo in the file. That is what the panels
// around this viewer are for — the colour a part was painted, the level a face came back at,
// a swatch held against a render. It was measurable that the alternative lied: a cushion
// whose atlas is identical top and bottom rendered at 218,211,205 from above and 160,147,139
// from below, the underside at 0.53 of its own texture on all three channels.
//
// STUDIO is the photograph: a key from above, a fill, and three's RoomEnvironment. It reads a
// shape — a diffuse gradient is how a leg is round and a dent is a dent — and it gives a
// metal something to reflect, which under even it has not got, a metallic surface having no
// diffuse term at all. A zinc-plated screw is flat grey under even and looks like metal here.
//
// Neither is the truth. Even is true about the file, studio is true about a room.
export type LightMode = 'even' | 'studio';

// THE CHOICE OUTLIVES THE VIEWER IT WAS MADE IN, like the shared angle beside it. Every mesh
// view in the app is one object being looked at from different pages, and a modal that opened
// evenly lit after you had just turned the studio on in the panel behind it would be the same
// question asked twice. Not persisted past the session: it is how you are looking right now,
// not a fact about the manual.
// STUDIO TO OPEN ON, everywhere. Even was the default because it is the honest one about the
// FILE — a surface at its own albedo, which is what you want when judging whether a texture
// came out the colour it was matched to. But that is one question asked occasionally, and the
// question every viewer in the app is opened for is "what is this part, and is its shape
// right" — which even cannot answer: a flat albedo has no gradient, so a round leg is a
// coloured silhouette and a dent is nothing at all. The assembly viewer already settled this
// for itself and offers no switch; this makes the rest of the app open where it does.
let chosenLight: LightMode = 'studio';
export const lightMode = () => chosenLight;
export const rememberLight = (mode: LightMode) => { chosenLight = mode; };

// GIVING THE WEBGL CONTEXT BACK, which `dispose()` alone does NOT do.
//
// A browser grants a page a HANDFUL of live contexts — sixteen in Chrome — and takes the
// OLDEST when a new one is asked for past the limit. `renderer.dispose()` frees the buffers
// and textures the renderer made and leaves the context itself alive until the garbage
// collector happens to reach the canvas, which may be a minute or may be never.
//
// This app builds them faster than that. Every mesh thumbnail is a renderer, every viewer is
// another, and one press of Determine orientation makes a seventh for six renders and drops
// it. So the count climbs while nothing looks wrong, and then the browser reclaims a context
// somebody is still using: the assembly viewport reports `ready`, holds a live scene, and
// draws white, with "Too many active WebGL contexts. Oldest context will be lost." in the
// console being the only evidence of what happened. The reload button in its corner exists
// for the case where this has already happened; this is what stops it happening.
//
// WEBGL_lose_context is what forceContextLoss uses and it is not universal — a browser
// without it is one where this was never a problem, so the failure to release is not one
// worth reporting.
//
// AND ITS DRAWING SURFACE, by shrinking the canvas to one pixel (Louis 2026-10-26). Losing the
// context does not free it: the surface goes with the canvas, when the browser's garbage collector
// gets round to it, and on an iPhone it never did within a pass of the tutorial. Thirty released
// views were all still waiting at the last step, some 0.5 GB of surfaces, and iOS killed the page
// on the next switch. Nothing draws on a released view again, so it gives up nothing.
export function releaseContext(renderer: import('three').WebGLRenderer) {
  renderer.dispose();
  try {
    renderer.forceContextLoss();
  } catch {
    /* no WEBGL_lose_context: nothing to give back by hand */
  }
  renderer.domElement.width = 1;
  renderer.domElement.height = 1;
  // After the shrink, so the tally counts what the released view still holds.
  untallyRenderer(renderer);
}

// BOTH LIGHTINGS AND THE SWITCH BETWEEN THEM, built onto a scene.
//
// Out here rather than inside mountMesh because a second viewer wanted them, and the two
// agreeing is the point rather than a tidiness: "even" is a claim about what is on screen —
// that a surface renders at its own albedo — and two viewers with their own answer to it
// would be two answers to "what colour is this part".
export function makeLighting(
  THREE: typeof import('three'),
  renderer: import('three').WebGLRenderer,
  scene: import('three').Scene,
  RoomEnvironment: typeof import('three/examples/jsm/environments/RoomEnvironment.js')['RoomEnvironment'],
) {
  const env = new THREE.PMREMGenerator(renderer);
  // A WHITE BOX SEEN FROM INSIDE, rendered once into a cube map. Uniform in every
  // direction, unlike the room — and still an environment rather than an ambient light,
  // because a metallic material has no diffuse at all and would render BLACK with nothing
  // to reflect. Converted CAD parts arrive metallic by default.
  const flat = new THREE.Scene();
  flat.add(new THREE.Mesh(
    new THREE.BoxGeometry(10, 10, 10),
    new THREE.MeshBasicMaterial({ color: 0xffffff, side: THREE.BackSide }),
  ));
  // Built once each, on first use, and kept: a PMREM pass is a render, and paying for it on
  // every press would make the toggle stutter on a large mesh.
  const maps: Partial<Record<LightMode, import('three').Texture>> = {};
  const mapFor = (mode: LightMode) => {
    if (!maps[mode]) {
      maps[mode] = env.fromScene(mode === 'even' ? flat : new RoomEnvironment(), 0.04).texture;
    }
    return maps[mode]!;
  };

  // WHAT THE STUDIO SET IS DIVIDED BY, so that the face the key is on renders at its own
  // albedo rather than well above it. The set below sums to roughly 1.7 on a normal turned
  // into the key — 0.35 of ambient, most of the key's 1.1, a little of the fill, and the
  // room at 0.55 of a map whose own mean is about half — and a surface lit at 1.7 reads far
  // above the swatch its colour was matched to. Three's ACES hid that by quietly dividing
  // exposure by 0.6 inside itself; the neutral curve does not, so the number is stated here
  // rather than being somebody else's side effect. It is the one number to turn if the
  // studio reads dark.
  const STUDIO_EXPOSURE = 0.6;

  // The photographic set, off until it is asked for. Built now rather than on demand because
  // three lights cost nothing to hold and the switch has to be instant.
  const ambient = new THREE.AmbientLight(0xffffff, 0.35);
  const key = new THREE.DirectionalLight(0xffffff, 1.1);
  key.position.set(3, 5, 4);
  const fill = new THREE.DirectionalLight(0xffffff, 0.4);
  fill.position.set(-4, 1, -3);
  [ambient, key, fill].forEach((l) => { l.visible = false; scene.add(l); });

  // HOW MUCH BRIGHTER THAN THE SET, for a caller whose reader asks (the published page). Over
  // the exposure and not over the lights: it is the picture that is turned up, the same
  // light in the same places, and the neutral curve goes on rolling off what that pushes
  // past white. 1 is the set as argued below, and is what everything else keeps.
  let gain = 1;
  let lit: LightMode = 'studio';
  const expose = () => {
    renderer.toneMappingExposure = (lit === 'even' ? 1 : STUDIO_EXPOSURE) * gain;
  };
  const brighten = (by: number) => { gain = by > 0 ? by : 1; expose(); };

  // THIS SCENE'S TONE SETTINGS PUT BACK on the renderer, which holds them for every scene it draws:
  // one renderer drawing several (Accueil's four tiles, see sharedRenderer) had each new tile's
  // lighting set its own exposure, unbrightened, and the first tile flickered darker until that
  // tile was told its brightness (Louis 2026-10-26). Called before each such draw.
  const restore = () => {
    renderer.toneMapping = lit === 'even' ? THREE.NoToneMapping : THREE.NeutralToneMapping;
    expose();
  };

  const apply = (mode: LightMode) => {
    lit = mode;
    scene.environment = mapFor(mode);
    // EVEN IS 1 AND THE ROOM IS 0.55, and the difference is not a taste. Under even, the
    // environment IS the whole of the light and a surface has to come out at its own
    // albedo; under the room it is the ambient half of a set whose key carries the rest, and
    // at 1 the part would sit well above the swatch its colour was matched to.
    scene.environmentIntensity = mode === 'even' ? 1 : 0.55;
    [ambient, key, fill].forEach((l) => { l.visible = mode === 'studio'; });

    // AND THE TONEMAPPER IS PART OF THE LIGHTING, which is why it is set here and nowhere
    // else. It is the last thing between a linear render and the pixels and it is not
    // neutral about colour: ACES filmic, which all three viewers used to set for
    // themselves, rolls the brightest channel off hardest, so it walks a pale colour
    // towards white.
    //
    // MEASURED, on the beige seat whose file holds #d9c5b6 — the colour its crops were
    // read at, to four levels. On screen it was #e0dad5: saturation 0.161 in the bytes
    // against 0.049 on the glass, two thirds of the colour gone. The dark wood beside it
    // came through the same curve untouched (0.430 against 0.418) because its channels sit
    // far below the shoulder. One tonemapper, and it only ever hurt the pale materials —
    // which is why the legs looked right in the same picture the seat looked bleached in.
    //
    // SO EVEN TONE-MAPS NOTHING. Under a uniform white sphere at intensity 1 no diffuse
    // surface can exceed 1, there is nothing to roll off, and this mode's whole claim is
    // that what is on screen IS what is in the file. With a curve in the way that claim
    // was false, and it is the claim the colour of every part gets judged against.
    //
    // AND STUDIO TONE-MAPS NEUTRALLY. A key light does push past 1 and does need rolling
    // off — a lit face landing flat on white with a hard edge is what put a curve here in
    // the first place — but Khronos's neutral curve compresses the peak and leaves hue and
    // saturation where they were, which is what it was written for. Same seat, same set:
    // saturation 0.159.
    renderer.toneMapping = mode === 'even' ? THREE.NoToneMapping : THREE.NeutralToneMapping;
    expose();
  };

  const dispose = () => {
    Object.values(maps).forEach((t) => t?.dispose());
    env.dispose();
    flat.traverse((o) => {
      const m = o as import('three').Mesh;
      m.geometry?.dispose?.();
      const mat = m.material;
      if (mat && !Array.isArray(mat)) mat.dispose();
    });
  };

  return { apply, brighten, restore, dispose };
}

// The loaded mesh, handed back so a caller can do its own work on it — the viewer builds
// its wireframes and counts triangles from exactly this.
// A camera, as three numbers and three more: where it stands and what it is pointed at.
// Enough to put a second viewer exactly here, and nothing about the mesh it was looking at.
export type Shot = { at: [number, number, number]; to: [number, number, number] };

export type LoadedMesh = {
  THREE: typeof import('three');
  root: import('three').Object3D;
  // THE CAMERA AND THE CANVAS IT DRAWS ON, for a caller with its own pass to run.
  //
  // Which is one caller: working out which texels of the atlas belong to the face you are
  // LOOKING at. That needs the camera, to know which way "facing me" is, and the renderer,
  // because the answer is found by drawing the mesh into UV space — and a second WebGL
  // context to do it in is the thing this app is careful not to spend. See facingMask.
  camera: import('three').PerspectiveCamera;
  renderer: import('three').WebGLRenderer;
  // TURNING THE VIEW WITHOUT DRAGGING IT.
  //
  // A drag is the right way to look for something and the wrong way to arrive somewhere
  // known. "The other side" and "back to how it opened" are two places anybody working on
  // a part goes over and over — and by hand each one is a wrist's worth of orbiting that
  // lands somewhere near, which for a face selection means the face was taken from an
  // angle nobody chose.
  //
  // `home` is the framing this viewer opened at, whatever has happened since. `turn` swings
  // the camera about a world axis THROUGH the object, so half a turn about Y is the back of
  // the part and half about X is its underside.
  home: () => void;
  turn: (axis: 'x' | 'y' | 'z', degrees: number) => void;
  // WHERE THE CAMERA IS STANDING, and how to put another one in the same place.
  //
  // A viewer is mounted per mesh, so every swap of `src` builds a scene and frames it
  // afresh — which is right for a different object and wrong for the SAME object at two
  // stages. Comparing a written model's draft against its correction means looking at both
  // from one angle: re-aiming by hand between every toggle is how a difference gets missed.
  //
  // The target goes with the position, because a camera restored without it orbits the next
  // drag around a point nobody is looking at.
  viewpoint: () => Shot;
  aimAt: (shot: Shot) => void;
  // RELIGHT WHAT IS ALREADY ON SCREEN. A swap of the environment map and three lights'
  // visibility — nothing is rebuilt, so the camera stays where it was and the two lightings
  // can be held against each other on the same view, which is the only way to see what
  // either of them is doing to the part.
  light: (mode: LightMode) => void;
};

// WHERE THE CAMERA IS, said in a way that survives being handed to a different viewer.
//
// Not a position, and not a distance either. A position is in the units of one mesh framed
// for one viewport, and carrying it from a 300-pixel panel to a full-screen modal would put
// the camera inside a screw or a mile from a chair. What travels is the ANGLE alone — a
// unit vector from the object — because that is the only part of a camera that means the
// same thing on a thumbnail, a panel and a full screen. Each view keeps its own distance.
export type MeshView = {
  dir: [number, number, number];
};

// THE MESHES ALREADY PARSED, newest last — keyed by the URL, which names exactly these bytes
// (see meshSrc, which stamps it, and _send_asset, which says so).
//
// WHY A CACHE AND NOT JUST THE BROWSER'S. An immutable Cache-Control stops the DOWNLOAD; it
// does nothing about the parse, and the parse is the expensive half. Stepping from pass 3 to
// pass 1 and back, or opening a tile you were looking at a minute ago, re-ran GLTFLoader over
// the whole file every time — seconds of a frozen main thread for a mesh the page had already
// built once.
//
// WHAT IS SHARED IS THE GEOMETRY, which is the megabytes and the GPU upload. Each mount gets
// `clone()`, which copies the object graph and shares the buffers, and then its OWN materials:
// a clone that shared those would have one viewer's wireframe or face colour appear in every
// other view of the same mesh.
//
// AND THE SHARED HALF IS NOT DISPOSED WITH THE VIEW. Unmounting frees the GPU buffers of
// everything in its scene — it must, these meshes leak visibly otherwise — so anything the
// pool owns is marked and skipped, and freed when the pool drops it instead.
//
// BOUNDED TWICE, by count and by bytes, and the bytes are the one that matters. Four is a
// strip's worth at the size a build is capped to (blender_ops.TRIANGLE_BUDGET, ~5 MB); four of
// the runs made BEFORE that cap is 400 MB of retained typed arrays and GPU buffers, which is a
// tab the browser kills rather than a cache. So the pool measures what it holds.
const POOL_MAX = 4;
const POOL_BYTES = 220 * 1024 * 1024;
const pool = new Map<string, { scene: import('three').Group; bytes: number }>();
const POOLED = 'noticePooled';

// What a parsed mesh actually costs: its attribute arrays and its indices. Textures are not
// counted — these builds carry none, and a reconstruction's one map is small beside a million
// triangles of position and normal.
function weigh(scene: import('three').Group): number {
  let bytes = 0;
  scene.traverse((child) => {
    const mesh = child as import('three').Mesh;
    if (!mesh.isMesh || !mesh.geometry) return;
    Object.values(mesh.geometry.attributes).forEach((a) => {
      bytes += (a as import('three').BufferAttribute).array?.byteLength ?? 0;
    });
    bytes += mesh.geometry.index?.array?.byteLength ?? 0;
  });
  return bytes;
}

function release(scene: import('three').Group): void {
  scene.traverse((child) => {
    const mesh = child as import('three').Mesh;
    if (!mesh.isMesh) return;
    mesh.geometry?.dispose();
    const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    mats.forEach((m) => {
      (m as import('three').MeshStandardMaterial)?.map?.dispose();
      m?.dispose();
    });
  });
}

// AND ONE PARSE PER FILE EVEN WHEN TWO VIEWS ASK AT ONCE. The viewer and the tile it was
// opened from are the same mesh mounting in the same frame; without this they are two fetches
// and two parses of one file, and the pool only starts saving from the third view onwards.
const loading = new Map<string, Promise<import('three').Group>>();

function keep(src: string, scene: import('three').Group): void {
  pool.set(src, { scene, bytes: weigh(scene) });
  let held = [...pool.values()].reduce((sum, one) => sum + one.bytes, 0);
  // The oldest go first, and the newest STAYS even alone over the budget: it is the mesh
  // somebody is looking at, and a pool that dropped it would be parsing it again next frame.
  while (pool.size > 1 && (pool.size > POOL_MAX || held > POOL_BYTES)) {
    const oldest = pool.keys().next().value as string;
    const going = pool.get(oldest);
    pool.delete(oldest);
    if (going) {
      held -= going.bytes;
      release(going.scene);
    }
  }
}

// ONE AT A TIME, FOR THE VIEWS THAT ASK FOR IT — see `queued` below.
//
// A GLB is fetched and then parsed ON THE MAIN THREAD: GLTFLoader builds every buffer,
// geometry and material in one go, and nothing else on the page moves while it does. One is
// a hitch. Four at once — which is what a strip of run thumbnails is, all mounting the moment
// they scroll in — is a page that will not scroll, and these meshes are not small: a written
// chair after a voxel remesh is a million triangles and a hundred megabytes.
//
// A promise chain and not a worker pool: the work is not parallelisable (it is the main
// thread) and serialising it is the whole point. The chain absorbs a rejection so one tile's
// bad file cannot stop the rest, and a task that never settles is let go of after a while for
// the same reason.
const QUEUE_GIVE_UP_MS = 60000;
let waiting: Promise<unknown> = Promise.resolve();

function inTurn(work: () => Promise<void>): void {
  const go = () => Promise.race([
    work(),
    new Promise((resolve) => setTimeout(resolve, QUEUE_GIVE_UP_MS)),
  ]);
  waiting = waiting.then(go, go);
}

export type MountOpts = {
  // LOAD IN TURN rather than at once. For a view that is one of several mounting together —
  // the run strip — where the alternative is every tile parsing a mesh in the same frame. The
  // viewer being LOOKED at never queues: it is the one the page is waiting for.
  queued?: boolean;
  // Blender's axis gizmo in the corner, turning with the view. It sizes itself to the
  // viewport now, so a thumbnail can have one too — which is worth more there than it used
  // to be, a tile that turns with the viewer above it being a tile you can lose your
  // bearings in.
  axes?: boolean;
  // WHAT EACH AXIS MEASURES, drawn on its arrow — "X (width)", in the arrow's own colour,
  // turning with it. Given, the gizmo is labelled and grows to hold the words; omitted, the
  // arrows are bare, which is what a thumbnail wants. See makeGizmo.
  axisWords?: { x: string; y: string; z: string };
  // The part's real size, for the measurement lines drawn around the object. Undefined
  // draws none; an object with null axes draws all three and says which are unknown.
  dims?: Dims;
  // The file is already the part, at true scale: a CAD conversion, which glTF puts in
  // metres. Then nothing needs measuring and no printed spec has to anchor it, which for
  // some parts is the only way they can be labelled at all. A spring washer's one printed
  // spec is "M6", and that is the screw it FITS rather than a size of its own: it names
  // the hole, and an M6 washer is 11.8 mm across in DIN 127B, 12 in DIN 125 and 18 in
  // DIN 9021. The designation pins nothing, and unlike a screw there is no length to fall
  // back on. The file knows; it was simply never asked.
  trueScale?: boolean;
  dimLabels?: DimLabels;
  // Whether the mouse turns and zooms the object. Off for thumbnails: they are read, not
  // driven, and a row of them that each grabbed the wheel would make the page unscrollable.
  interactive?: boolean;
  // The mesh's extents in the FILE's own units, once it has loaded. What a caller needs
  // to work out a scale to write back into that file.
  onMeasure?: (size: Extent) => void;
  onLoaded?: (mesh: LoadedMesh) => void;
  // WHERE THE POINTER IS ON THE TEXTURE, as [u, v], or null when it is over no surface.
  //
  // The one mapping a mesh already carries: every vertex holds the coordinate in the
  // texture that its corner of the picture came from, so a ray that hits a triangle can be
  // asked which texel it landed on. What that is FOR is the atlas — a flayed square of wood
  // in which nothing is recognisable — shown beside the object it was cut from, so pointing
  // at a leg says which part of the square is that leg.
  //
  // Only this direction is cheap. The reverse — a point on the atlas back to a spot on the
  // mesh — has to search the triangles for the one whose UVs contain it, and nothing in the
  // file indexes them that way.
  onHoverUv?: (uv: [number, number] | null) => void;
  onState?: (state: 'ready' | 'failed') => void;
  // JOIN THE ONE ANGLE EVERY MESH IN THE APP IS TURNED TO — see meshView.
  //
  // What the view does with it follows from `interactive`, because that is already the
  // difference between the two kinds of view here. An interactive one DRIVES: it opens at
  // the shared angle and reports back whenever a turn or a zoom settles. A thumbnail
  // FOLLOWS: it opens at the same angle and is re-aimed when the angle moves, having no
  // controls of its own to fight over it.
  sharedView?: boolean;
};

import {
  holdLabels, makeDimensions, makeGizmo, mmScale, wantedDims,
  type DimLabels, type Dims, type Extent,
} from './meshAnnotations';
import { getMeshView, setMeshView, subscribeMeshView } from './meshView';
import { IS_SANDBOX } from '../../apiBase';
import { packedLoader } from './meshLoader';
import { isPublicTutorialRoute } from '../../publicRoute';
import { tallyFileCache, tallyRenderer, untallyRenderer } from './gpuTally';

// Draw `src` inside `el`, and answer the function that takes it all down again.
//
// Synchronous to call and asynchronous inside, so an effect can return the result
// directly: everything after the import checks `alive`, and a component unmounted while
// three.js is still loading tears down nothing because nothing was built.
export function mountMesh(el: HTMLElement, src: string, opts: MountOpts = {}): () => void {
  let alive = true;
  let cleanup: (() => void) | undefined;

  void loadDeps()
    .then(({ THREE, GLTFLoader, OrbitControls, RoomEnvironment }) => {
      if (!alive) return;

      const scene = new THREE.Scene();
      const camera = new THREE.PerspectiveCamera(45, 1, 0.01, 1000);
      // AND THE DRAWING BUFFER IS KEPT ON THE SANDBOX, where it is the difference between
      // a save that has the 3D in it and one with a grey rectangle where the viewport was.
      // A WebGL canvas is read back with toDataURL, and a browser is free to throw the
      // colour buffer away the moment it has composited — which it does, unless asked not
      // to. The Saves tab photographs a whole phase some time after it was drawn (see
      // sandboxSaves), so it cannot use the trick the clip poster uses of rendering and
      // reading inside one task. It costs a copy per frame and it is paid on the sandbox
      // alone, which is the one build that photographs itself.
      const renderer = new THREE.WebGLRenderer({
        antialias: true, alpha: true, preserveDrawingBuffer: IS_SANDBOX,
      });
      tallyRenderer(renderer);
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
      renderer.outputColorSpace = THREE.SRGBColorSpace;
      // HIGHLIGHTS ROLL OFF INSTEAD OF CLIPPING, and WHICH curve does it is the lighting's
      // own business — see makeLighting, which argues the choice and which the two other
      // viewers share, so there is one answer to it rather than three.
      el.appendChild(renderer.domElement);
      renderer.domElement.style.width = '100%';
      renderer.domElement.style.height = '100%';
      renderer.domElement.style.display = 'block';

      // LIGHT FROM EVERYWHERE, EQUALLY. No key, no fill, no room: a uniform white sphere of
      // light around the object, so every surface receives exactly the same irradiance
      // whichever way it points.
      //
      // WHAT THIS VIEWER IS FOR decides it. It was lit like a photograph — a key from above
      // at 1.1, a fill, and three's RoomEnvironment, which is a room and so is bright at the
      // ceiling and dark at the floor. That reads a shape beautifully and lies about a
      // surface, and this panel's job is the surface: the colour a part was painted, the
      // texture a reconstruction gave it, the face that came back at the wrong level.
      //
      // The lie was measurable. A seat cushion whose atlas is IDENTICAL top and bottom —
      // RGB(215, 197, 185) either side, every percentile within one level — rendered at
      // 218,211,205 from above and 160,147,139 from below. In linear light the underside came
      // out at 0.53 of its own texture, the same 0.53 on all three channels, because neither
      // directional reaches a downward normal and the room's floor is dark. Two faces of one
      // colour, one of which looked "much darker", and the panel measuring them was right
      // both times while the picture beside it disagreed.
      //
      // AND THE OBJECT NEVER MOVES, which is what made it permanent rather than a passing
      // angle: OrbitControls swings the CAMERA and the lights stand still, so the lit side is
      // lit for ever and turning the part shows you the dark one rather than lighting it.
      //
      // At intensity 1 a surface renders as its own albedo, which is the property worth
      // having here: what is on screen IS what is in the file, and a colour matched to a
      // swatch arrives on screen as that swatch.
      //
      // AND IT TOOK THE TONEMAPPER WITH IT, because for a while that sentence was simply
      // untrue. The light was 1 and the albedo did survive it, and then ACES filmic — set
      // right here, three lines up, for the sake of the studio mode's highlights — pulled
      // a beige seat from #d9c5b6 to #e0dad5 on its way to the glass. Even mode now
      // tone-maps nothing at all; see makeLighting, which owns both halves of the claim.
      //
      // WHAT IT COSTS is form. A diffuse gradient is how a leg reads as round and a dent
      // reads as a dent, and there is none of it now — the silhouette and the wireframe modes
      // carry the geometry alone. A pale part on this viewer's white background is close to
      // losing its own outline. If that turns out to matter more than the colour does, a
      // single weak directional brings the shape back; it is one line, and it costs exactly
      // the fidelity above.
      // BOTH OF THEM, AND A SWITCH — because the trade above is real in both directions and
      // it is not the same answer twice in a row. Judging a colour wants even; judging a
      // shape, or looking at a metal part that has nothing to reflect under even, wants the
      // room back. It is one press now (see `light`), and the choice follows you to the next
      // mesh you open.
      const lighting = makeLighting(THREE, renderer, scene, RoomEnvironment);
      const applyLight = lighting.apply;
      applyLight(lightMode());

      const controls = opts.interactive ? new OrbitControls(camera, renderer.domElement) : null;
      if (controls) {
        controls.enableDamping = true;
        // THE CAMERA ONLY TURNS, as in every viewer (see assemblyScene's note over PartPick): no
        // pan, and the wheel zooms toward the point under the cursor.
        controls.enablePan = false;
        controls.zoomToCursor = true;
      }

      const resize = () => {
        const { clientWidth: w, clientHeight: h } = el;
        if (!w || !h) return;
        camera.aspect = w / h;
        camera.updateProjectionMatrix();
        renderer.setSize(w, h, false);
      };
      const observer = new ResizeObserver(resize);
      observer.observe(el);
      resize();

      // Built before the mesh loads so the first frame already has it: the gizmo says
      // which way the view is pointing, and that is worth knowing while a mesh is still
      // arriving.
      const gizmo = opts.axes ? makeGizmo(THREE, renderer, opts.axisWords) : null;
      let annotations: { group: import('three').Object3D; dispose: () => void } | null = null;
      let hoverOff: (() => void) | undefined;
      let glideOff: (() => void) | undefined;
      // Set once the object is framed, because that is when there is an angle to report.
      let viewOff: (() => void) | undefined;

      // A TURN THAT ARRIVES INSTEAD OF BEING DRAGGED, slid rather than cut. Half a turn of
      // a nearly symmetrical stool, applied in one frame, looks like nothing happened — the
      // movement IS how you know which way it went and that it went at all.
      //
      // The angle travels as an AXIS AND AN ANGLE, not as two endpoints lerped. Two opposite
      // directions have no midpoint: lerping them passes through the origin, where the
      // camera's own position is undefined and lookAt has nothing to aim along. Exactly the
      // half-turn this exists for.
      let gliding: {
        from: import('three').Vector3;
        axis: import('three').Vector3;
        angle: number;
        fromLen: number;
        toLen: number;
        t0: number;
      } | null = null;
      const GLIDE_MS = 380;

      let raf = 0;
      const tick = () => {
        if (gliding) {
          const k = Math.min(1, (performance.now() - gliding.t0) / GLIDE_MS);
          // Smoothstep, so it leaves and arrives at rest. A linear turn stops dead at the
          // end and reads as a jump backwards on the last frame.
          const e = k * k * (3 - 2 * k);
          camera.position.copy(gliding.from)
            .applyAxisAngle(gliding.axis, gliding.angle * e)
            .setLength(gliding.fromLen + (gliding.toLen - gliding.fromLen) * e);
          camera.lookAt(0, 0, 0);
          if (k >= 1) gliding = null;
        }
        controls?.update();
        holdLabels(scene, camera, renderer.domElement.clientHeight);
        renderer.render(scene, camera);
        // After the main pass, over it, with its own depth: the gizmo is an overlay and
        // must not be occluded by whatever the object happens to be doing in that corner.
        gizmo?.render(camera, controls?.target ?? new THREE.Vector3());
        raf = requestAnimationFrame(tick);
      };
      tick();

      // Settled when this mesh is on screen or has failed — which is when the next queued
      // tile may start. Called once: a second call is a task that has already let go.
      let done = () => {};
      const load = () => new Promise<void>((settle) => {
        done = () => { settle(); done = () => {}; };
        if (!alive) return done();
        mount();
      });

      // WHAT EVERY MOUNT WORKS ON: a clone of the scene as it was parsed, never the parsed
      // scene itself. The body below scales it, stands it up and centres it — done twice to
      // one object, that is a mesh at a twentieth of its size in the wrong place.
      const show = (pristine: import('three').Group) => {
          if (!alive) return done();
          const root = pristine.clone();
          root.traverse((child) => {
            const mesh = child as import('three').Mesh;
            if (!mesh.isMesh) return;
            // The geometry belongs to the pool; this view must not free it. Its materials are
            // its own, so what a viewer does to them stays in that viewer.
            mesh.userData[POOLED] = true;
            const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
            const own = mats.map((m) => m?.clone() ?? m);
            mesh.material = Array.isArray(mesh.material) ? own : own[0];
          });

          // Put the mesh in MILLIMETRES, if the drawing measured anything at all.
          //
          // A generated mesh arrives normalized to about one unit across, which is a
          // proportion and not a size — so the drawing's number is the only real one
          // there is, and matching one axis to it makes the other two real as well. Done
          // before the framing below so the camera works on the final object; it changes
          // nothing on screen, since the framing fits whatever it is given.
          const raw = new THREE.Box3().setFromObject(root).getSize(new THREE.Vector3());
          // Through mmScale, which the scale buttons ask as well: the labels and the
          // buttons must be looking at the same millimetres.
          // NO `builtMm` HERE. The viewers that mount this way — the SAM result, the
          // texture retouch — show reconstructions, which carry no measures
          // of their own: the parameter would be one nothing ever passed. The written engine's
          // meshes go through assemblyScene (see PartViewer).
          const scale = mmScale(raw, opts.dims, opts.trueScale);
          if (scale) root.scale.setScalar(scale);
          // The extents BEFORE any scaling: the file's own units, which is what a caller
          // needs to compute a scale to bake back into that file.
          opts.onMeasure?.({ x: raw.x, y: raw.y, z: raw.z });

          // Z-UP, LIKE EVERY OTHER PICTURE OF A MESH IN THIS APP — and after the
          // measurement above, which is taken in the FILE's axes and would come back with
          // two of them swapped from under a rotated object.
          //
          // This viewer was the last thing here rendering a mesh as three does by default,
          // with +Y up. Everything else is Z-up: the assembly scene turns its root a quarter
          // about X, the gizmo letters its arms "Z (height)", and meshAnnotations.FACES puts
          // "top" on +z. So one part had two pictures on one page disagreeing about which
          // way it stands, and the shared angle underneath them could not fix it — a
          // direction means a different thing in each of two scenes whose ups differ, and no
          // choice of direction makes a Z-up object look upright in a Y-up frame.
          //
          // What this costs is that a mesh whose FILE is not Z-up now looks tipped over in
          // its thumbnail. That is the honest reading: the app's frame is the product's, a
          // reconstruction arrives in whatever frame its picture implied, and a tile that
          // quietly stood such a part upright was showing something no other view agreed
          // with. It is also exactly what the orientation button is for.
          root.rotation.x = -Math.PI / 2;

          // Frame the object: centre it on the origin, then pull the camera back far
          // enough for its bounding sphere. Meshes arrive at wildly different scales, so
          // a fixed camera position would show half of them as a dot and the rest from
          // inside. Every view uses the same angle, which is what makes two runs of one
          // part comparable side by side.
          const box = new THREE.Box3().setFromObject(root);
          const sphere = box.getBoundingSphere(new THREE.Sphere());
          root.position.sub(sphere.center);
          const dist = sphere.radius / Math.sin((camera.fov * Math.PI) / 360);
          camera.position.set(0, sphere.radius * 0.35, dist * 1.25);
          camera.near = dist / 100;
          camera.far = dist * 100;
          // How far out THIS viewer framed THIS mesh. Read off the camera rather than
          // recomputed from the sphere, so the framing above and the aiming below cannot
          // drift apart.
          const home = camera.position.length();
          // AND WHERE, not only how far: what "back to normal" means for this viewer, kept
          // before the shared angle is applied over it. The framing is this mesh's own —
          // its bounding sphere sets both the height and the distance — so it cannot be
          // written down as a constant anywhere.
          const opened = camera.position.clone();
          // Swing the camera round to the shared angle, keeping that distance.
          const aim = (view: MeshView) => {
            if (!(home > 1e-9)) return;
            const dir = new THREE.Vector3(...view.dir);
            if (dir.lengthSq() <= 1e-9) return;
            dir.normalize();
            // STRAIGHT DOWN THE POLE IS NOT A VIEW lookAt can resolve: the camera's up
            // vector is (0,1,0), and a camera looking along its own up has no way to know
            // which way round it goes. That is a NaN orientation and an empty frame, and
            // OrbitControls hands out that angle happily — its polar limits are the full
            // half-turn by default. Tipped off the pole by a whisker instead: invisible,
            // and always resolvable.
            //
            // ALONG ITS OWN AZIMUTH, which is the part that took two goes. Tipping toward a
            // FIXED side threw away the one thing still distinguishing one overhead view
            // from another: looking straight down, the direction no longer says which way
            // round the object sits, and the whole of that answer is in where the camera
            // came from. So the strip snapped to a stranger's angle exactly as the viewer
            // reached its limit — the two pictures agreeing until the moment somebody
            // pushed the orbit as far as it goes, and disagreeing there.
            //
            // The viewer never truly reaches the pole (OrbitControls clamps its polar angle
            // just short of it), so that azimuth is always there to be kept.
            if (Math.abs(dir.y) > 0.9999) {
              const azimuth = dir.x === 0 && dir.z === 0 ? 0 : Math.atan2(dir.z, dir.x);
              dir.set(
                Math.cos(azimuth) * 0.0001, dir.y > 0 ? 1 : -1, Math.sin(azimuth) * 0.0001,
              ).normalize();
            }
            // ALWAYS AT THIS VIEWER'S OWN DISTANCE. The angle travels between views; how
            // far out you had pulled does not, and must not — meshes arrive at wildly
            // different scales, so a distance chosen while inspecting a thread frames a
            // chair from inside it.
            camera.position.copy(dir.multiplyScalar(home));
            // AND POINT IT AT THE OBJECT, which is the half a thumbnail was missing. The
            // default framing sits on the +Z axis, so a camera left in its own default
            // orientation — looking down -Z — happened to face the origin anyway, and
            // nothing here ever had to say so. An angle somebody turned to is under no
            // such obligation: moved to the side and still looking down -Z, a thumbnail
            // aimed past the mesh into empty space and rendered white.
            //
            // Interactive views never showed it because controls.update() re-aims from the
            // target itself. Thumbnails have no controls, which is exactly why they broke.
            camera.lookAt(0, 0, 0);
            camera.updateProjectionMatrix();
            controls?.update();
          };
          // Null until somebody has turned a mesh: then the framing above stands,
          // which is the same first look every mesh has always opened at.
          const stored = opts.sharedView ? getMeshView() : null;
          if (stored) aim(stored);
          camera.updateProjectionMatrix();
          controls?.target.set(0, 0, 0);
          controls?.update();
          // NOT TURNED BY HAND: a drag anywhere, the object included, turns the camera.
          scene.add(root);

          // POINTING AT THE OBJECT SAYS WHERE THAT IS ON THE TEXTURE. A ray from the cursor
          // into the scene; three.js hands back the hit triangle's interpolated `uv`, which
          // is the coordinate in the picture that point of wood was painted from.
          //
          // Off the element and not the window, and cleared on leave, so the marker on the
          // atlas goes out when the pointer is no longer on the mesh rather than freezing
          // wherever it was last seen — which reads as a claim that is no longer being made.
          if (opts.onHoverUv) {
            const ray = new THREE.Raycaster();
            const at = new THREE.Vector2();
            const onPointer = (e: PointerEvent) => {
              const r = renderer.domElement.getBoundingClientRect();
              if (!r.width || !r.height) return;
              at.set(((e.clientX - r.left) / r.width) * 2 - 1,
                     -((e.clientY - r.top) / r.height) * 2 + 1);
              ray.setFromCamera(at, camera);
              // THE NEAREST HIT THAT IS A SURFACE, not the nearest hit. The viewer hangs a
              // wireframe LineSegments off every mesh as a CHILD, so walking the subtree
              // finds those too — and a line intersection carries no uv, is tested with a
              // one-world-unit threshold that is enormous against a half-metre chair, and
              // sorts to the front. Taking [0] therefore found a wire, read no uv from it,
              // and reported "over nothing" for every pixel of the object.
              //
              // Intersections come back sorted by distance, so the first with a uv on it is
              // the closest piece of actual surface under the cursor.
              const hit = ray.intersectObject(root, true).find((i) => i.uv);
              opts.onHoverUv?.(hit?.uv ? [hit.uv.x, hit.uv.y] : null);
            };
            const onLeave = () => opts.onHoverUv?.(null);
            renderer.domElement.addEventListener('pointermove', onPointer);
            renderer.domElement.addEventListener('pointerleave', onLeave);
            hoverOff = () => {
              renderer.domElement.removeEventListener('pointermove', onPointer);
              renderer.domElement.removeEventListener('pointerleave', onLeave);
            };
          }

          if (opts.sharedView && home > 1e-9) {
            if (controls) {
              // On SETTLE and not every frame. A drag is sixty of these a second, and each
              // one writes to localStorage and wakes every thumbnail on the page.
              // The DIRECTION only. Zooming is left to each view: the thumbnails are
              // 96 pixels and the modal is a screen, and one distance cannot serve both.
              const report = (persist: boolean) => {
                const p = camera.position;
                const len = p.length();
                if (!(len > 1e-9)) return;
                setMeshView({ dir: [p.x / len, p.y / len, p.z / len] }, persist);
              };
              // ON EVERY FRAME OF THE TURN, so what follows follows smoothly — including
              // through the damping after the mouse is released, which is where most of
              // the movement people actually watch happens. `change` fires only when the
              // camera has really moved, so an idle view costs nothing.
              const onChange = () => report(false);
              // And SAVED once, when it settles. See setMeshView.
              const onEnd = () => report(true);
              controls.addEventListener('change', onChange);
              controls.addEventListener('end', onEnd);
              viewOff = () => {
                controls.removeEventListener('change', onChange);
                controls.removeEventListener('end', onEnd);
              };
            } else {
              // No controls to fight, so this one simply follows. Only the driven view
              // subscribes to nothing and only the followers subscribe to it: a viewer
              // that both reported and listened would re-aim itself mid-drag from its own
              // report.
              viewOff = subscribeMeshView(aim);
            }
          }

          // Measured off the CENTRED object, so the lines sit around what is on screen
          // rather than around where the file happened to put it.
          if (opts.dimLabels) {
            const framed = new THREE.Box3().setFromObject(root);
            // The mesh's own size in the manual's convention — x width, y depth, z height —
            // which is the file's own, this viewer being Z-up and mesh_orient writing +z as
            // the top. It used to trade y and z here, from when the viewer was Y-up, and the
            // labels then compared a height against a depth. Only meaningful once the scale
            // above has been applied — without it these are model units.
            const extent = framed.getSize(new THREE.Vector3());
            // To a tenth of a millimetre, as the measured size is read everywhere else (the
            // insertion table, the model-size record).
            const tenth = (v: number) => Math.round(v * 10) / 10;
            const measured = scale
              ? { x: tenth(extent.x), y: tenth(extent.y), z: tenth(extent.z) }
              : null;
            // WHAT THE PART IS SUPPOSED TO BE, on three axes. Straight through for a
            // component, whose dimension read answers x/y/z; assembled from `lengthMm` and
            // `bodyDiaMm` for a cylinder, which is how every piece of hardware in the app
            // is written down. See wantedDims.
            //
            // This used to COLLAPSE instead. Where the mesh was the only source — a
            // converted STEP, or a part with no axes — both rows became one, and that one
            // was the mesh's own number wearing the drawing's label. It read as agreement
            // between two sources when there was only ever one, and it hid the disagreement
            // in the case that now has two: a supplier's STEP file is 32 mm long while the
            // record says 40, and nothing on screen said so.
            annotations = makeDimensions(
              THREE, framed, wantedDims(opts.dims, measured), opts.dimLabels, measured,
            );
            scene.add(annotations.group);
          }

          // THE ROTATION THAT TAKES ONE DIRECTION TO ANOTHER. Antipodal ones have no unique
          // answer — every axis perpendicular to them turns one into the other — so a
          // perpendicular is chosen rather than leaving the cross product at zero, which
          // would be a "go home" that did nothing from the one place you most want it.
          const between = (a: import('three').Vector3, b: import('three').Vector3) => {
            const from = a.clone().normalize();
            const to = b.clone().normalize();
            const axis = new THREE.Vector3().crossVectors(from, to);
            if (axis.lengthSq() < 1e-12) {
              axis.crossVectors(new THREE.Vector3(0, 1, 0), from);
              if (axis.lengthSq() < 1e-12) axis.crossVectors(new THREE.Vector3(1, 0, 0), from);
            }
            return { axis: axis.normalize(), angle: Math.acos(Math.min(1, Math.max(-1, from.dot(to)))) };
          };

          const glide = (axis: import('three').Vector3, angle: number, toLen: number) => {
            const fromLen = camera.position.length();
            if (!(fromLen > 1e-9) || !(toLen > 1e-9)) return;
            gliding = { from: camera.position.clone(), axis, angle, fromLen, toLen, t0: performance.now() };
          };

          // A HAND ON THE MOUSE WINS. Left running, the glide would go on turning the camera
          // under a drag that is also turning it, and the two would fight for as long as it
          // lasted.
          if (controls) {
            const stop = () => { gliding = null; };
            controls.addEventListener('start', stop);
            glideOff = () => controls.removeEventListener('start', stop);
          }

          opts.onLoaded?.({
            THREE,
            root,
            camera,
            renderer,
            // BACK TO THE DISTANCE AS WELL AS THE ANGLE. "Normal" after twenty minutes of
            // work usually means zoomed into a leg as much as it means turned round.
            home: () => {
              const { axis, angle } = between(camera.position, opened);
              glide(axis, angle, opened.length());
            },
            light: applyLight,
            viewpoint: () => ({
              at: [camera.position.x, camera.position.y, camera.position.z],
              to: controls
                ? [controls.target.x, controls.target.y, controls.target.z]
                : [0, 0, 0],
            }),
            aimAt: (put) => {
              camera.position.set(put.at[0], put.at[1], put.at[2]);
              if (controls) {
                controls.target.set(put.to[0], put.to[1], put.to[2]);
                controls.update();
              } else {
                camera.lookAt(put.to[0], put.to[1], put.to[2]);
              }
              camera.updateProjectionMatrix();
            },
            turn: (which, degrees) => {
              const axis = new THREE.Vector3(
                which === 'x' ? 1 : 0, which === 'y' ? 1 : 0, which === 'z' ? 1 : 0,
              );
              glide(axis, (degrees * Math.PI) / 180, camera.position.length());
            },
          });
          opts.onState?.('ready');
          done();
      };

      const mount = () => {
        const had = pool.get(src);
        if (had) {
          // Newest last, so the pool drops what has not been looked at rather than what has.
          pool.delete(src);
          pool.set(src, had);
          return show(had.scene);
        }
        let going = loading.get(src);
        if (!going) {
          going = new Promise<import('three').Group>((ok, no) => {
            new GLTFLoader().load(src, (gltf) => ok(gltf.scene), undefined, no);
          }).then((scene) => {
            keep(src, scene);
            return scene;
          });
          loading.set(src, going);
          void going.catch(() => {}).then(() => loading.delete(src));
        }
        void going.then(show, (e) => {
          // The panel says one thing for every way this can go wrong, because there is
          // one thing the reader can do about it. The console gets the difference: a GLB
          // that failed to fetch and one that failed to parse are the same sentence on
          // screen and different bugs, and neither was recoverable from a swallowed error.
          console.error('[notice] mesh failed to load', src, e);
          if (alive) opts.onState?.('failed');
          done();
        });
      };

      if (opts.queued) inTurn(load);
      else void load();

      cleanup = () => {
        cancelAnimationFrame(raf);
        observer.disconnect();
        viewOff?.();
        hoverOff?.();
        glideOff?.();
        controls?.dispose();
        gizmo?.dispose();
        annotations?.dispose();
        // Free the GPU buffers: these views open one mesh after another, and meshes this
        // size leak visibly if their geometries and textures are only dropped by GC.
        scene.traverse((child) => {
          const mesh = child as import('three').Mesh;
          if (!mesh.isMesh && !(child as import('three').LineSegments).isLineSegments) return;
          // EXCEPT WHAT THE POOL OWNS. A pooled mesh's geometry and its maps are shared with
          // every other view of the same file and with the next one to open it; freed here,
          // the tile still on screen beside this one goes blank. Its materials are this
          // view's own clones and are freed like anything else. See `pool`.
          const shared = !!mesh.userData?.[POOLED];
          if (!shared) mesh.geometry?.dispose();
          const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
          mats.forEach((m) => {
            const withMap = m as import('three').MeshStandardMaterial;
            if (!shared) withMap?.map?.dispose();
            m?.dispose();
          });
        });
        // The environment is a cube map on the GPU and its generator holds a render target
        // of its own — neither is reached by the scene walk above, since neither is a mesh,
        // and both leak per viewer without this. These views open one after another.
        //
        // EVERY MAP BUILT, not the one in use: a viewer the lighting was switched in twice
        // holds two, and the one it is not showing leaks exactly as easily as the one it is.
        // Which is makeLighting's own business now, along with the flat box it renders.
        scene.environment = null;
        lighting.dispose();
        // The context back to the browser, not merely the buffers — see releaseContext.
        releaseContext(renderer);
        renderer.domElement.remove();
      };
    })
    .catch((e) => {
      // Everything before the loader: three.js failing to import, and — the one worth
      // recognising — `new WebGLRenderer()` throwing because the browser will not grant
      // another context. A page with a strip of mesh thumbnails and a viewer open is
      // already holding several, and that failure reads exactly like a broken file.
      console.error('[notice] mesh scene could not start', src, e);
      if (alive) opts.onState?.('failed');
    });

  return () => {
    alive = false;
    cleanup?.();
  };
}
